/**
 * Markdown renderer for chat messages with interactive block support.
 *
 * Wraps ReactMarkdown and intercepts fenced code blocks tagged
 * `question` or `decision` to render them as interactive UI components.
 * Falls back to standard code rendering if parsing fails.
 */
import { useRef, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { cn } from '@/lib/utils';
import { parseInteractiveBlock, countInteractiveBlocks, getContentUpToBlock } from './interactiveBlocks';
import { QuestionCard } from './QuestionCard';
import { DecisionButtons } from './DecisionButtons';

/** Selection state for a single interactive block */
export interface BlockSelection {
  selectedIndex: number;
  selectedText: string;
}

interface ChatMarkdownProps {
  content: string;
  /** Whether interactive blocks should be clickable */
  interactive?: boolean;
  /** Map of block index → selection state for answered blocks */
  answeredBlocks?: Map<number, BlockSelection>;
  /** The next user message content (for reconstructing selections on past blocks) */
  userReply?: string;
}

/**
 * Reconstruct which option was selected by matching the user's reply
 * against the block's options. Returns a BlockSelection or undefined.
 */
function reconstructSelection(
  block: ReturnType<typeof parseInteractiveBlock>,
  userReply: string,
): BlockSelection | undefined {
  if (!block) return undefined;
  // The reply may contain multiple selections separated by newlines (multi-block messages)
  // or commas (multi-select). Try exact option match first.
  const replyLines = userReply.split('\n').map(l => l.trim()).filter(Boolean);
  // For single-select: check if any reply line matches an option exactly
  for (const line of replyLines) {
    const idx = block.options.findIndex(
      opt => opt.toLowerCase() === line.toLowerCase(),
    );
    if (idx !== -1) {
      return { selectedIndex: idx, selectedText: block.options[idx] };
    }
  }
  // For multi-select: check if any reply line is a comma-separated list of options
  for (const line of replyLines) {
    const parts = line.split(',').map(p => p.trim());
    if (parts.length > 1) {
      const matchedIdx = parts
        .map(p => block.options.findIndex(o => o.toLowerCase() === p.toLowerCase()))
        .find(i => i !== -1);
      if (matchedIdx !== undefined && matchedIdx !== -1) {
        return { selectedIndex: matchedIdx, selectedText: line };
      }
    }
  }
  // No option matched — the user typed a custom "Other" response
  // Use the first reply line as the custom text
  if (replyLines.length > 0) {
    return { selectedIndex: -1, selectedText: replyLines[0] };
  }
  return undefined;
}

export function ChatMarkdown({
  content,
  interactive = false,
  answeredBlocks,
  userReply,
}: ChatMarkdownProps) {
  // Counter to assign sequential indices to interactive blocks in a render
  const blockIndexRef = useRef(0);
  blockIndexRef.current = 0;

  // Show only answered blocks inline — the pending question renders at the bottom (ChatBox input area)
  const visibleContent = useMemo(() => {
    if (!interactive) return content;
    const totalBlocks = countInteractiveBlocks(content);
    if (totalBlocks === 0) return content;
    const answeredCount = answeredBlocks?.size ?? 0;
    if (answeredCount >= totalBlocks) return content;
    return getContentUpToBlock(content, answeredCount);
  }, [content, interactive, answeredBlocks]);

  return (
    <div className={cn(
      "prose prose-sm dark:prose-invert max-w-none prose-table:w-full prose-table:border-collapse prose-th:border prose-th:border-border prose-th:bg-muted prose-th:px-3 prose-th:py-1.5 prose-th:text-start prose-th:font-semibold prose-td:border prose-td:border-border prose-td:px-3 prose-td:py-1.5 prose-td:text-start",
    )}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          // Override <pre> to avoid wrapping interactive blocks in preformatted text
          pre({ children: preChildren }) {
            return <>{preChildren}</>;
          },

          // Override <code> to detect and render interactive blocks
          code({ className, children: codeChildren }) {
            const codeContent = String(codeChildren).replace(/\n$/, '');
            const block = parseInteractiveBlock(className, codeContent);

            if (!block) {
              // Not an interactive block — render as normal inline/block code
              if (className) {
                // Fenced code block (has language class)
                return (
                  <pre>
                    <code className={className}>{codeContent}</code>
                  </pre>
                );
              }
              return <code>{codeContent}</code>;
            }

            // Assign an index to this interactive block
            const blockIndex = blockIndexRef.current++;
            const selection = answeredBlocks?.get(blockIndex);

            // Past messages: try to reconstruct selection from user reply
            if (!interactive && !selection && userReply) {
              const reconstructed = reconstructSelection(block, userReply);
              if (reconstructed) {
                // eslint-disable-next-line i18next/no-literal-string -- CSS class names, not user-facing
                const separatorClass = blockIndex > 0 ? 'my-3 pt-3 border-t border-border/40' : 'my-3';
                if (block.type === 'question') {
                  return (
                    <div className={separatorClass}>
                      <QuestionCard
                        question={block.question}
                        options={block.options}
                        allowOther
                        interactive={false}
                        selectedIndex={reconstructed.selectedIndex}
                        selectedOtherText={reconstructed.selectedText}
                        onSelect={() => {}}
                      />
                    </div>
                  );
                }
                return (
                  <div className={separatorClass}>
                    <DecisionButtons
                      question={block.question}
                      options={block.options}
                      interactive={false}
                      selectedIndex={reconstructed.selectedIndex}
                      onSelect={() => {}}
                    />
                  </div>
                );
              }
            }

            // Past messages with no selection and no user reply: show as static
            if (!interactive && !selection) {
              return (
                <div className="my-3 p-2.5 rounded-lg bg-muted/50 text-sm">
                  <p className="font-medium mb-1.5">{block.question}</p>
                  {block.options.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {block.options.map((opt: string, i: number) => (
                        <span key={i} className="inline-flex px-2.5 py-1 rounded-full text-xs border border-border/60 text-muted-foreground bg-background/50">
                          {opt}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              );
            }

            // Answered block: show compact selected-state card inline
            if (selection) {
              // eslint-disable-next-line i18next/no-literal-string -- CSS class names, not user-facing
              const separatorClass = blockIndex > 0 ? 'my-3 pt-3 border-t border-border/40' : 'my-3';

              if (block.type === 'question') {
                return (
                  <div className={separatorClass}>
                    <QuestionCard
                      question={block.question}
                      options={block.options}
                      allowOther
                      interactive={false}
                      selectedIndex={selection.selectedIndex}
                      selectedOtherText={selection.selectedText}
                      onSelect={() => {}}
                    />
                  </div>
                );
              }

              return (
                <div className={separatorClass}>
                  <DecisionButtons
                    question={block.question}
                    options={block.options}
                    interactive={false}
                    selectedIndex={selection.selectedIndex}
                    onSelect={() => {}}
                  />
                </div>
              );
            }

            // Should not reach here (pending blocks are rendered at the bottom)
            return null;
          },
        }}
      >
        {visibleContent}
      </ReactMarkdown>
    </div>
  );
}
